<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit, { type ITableSortSettings, type ITableColumn } from '@pocketprep/ui-kit'
import type { Study, Teach } from '@pocketprep/types'
import GroupsDropdown from '@/components/SidePanels/EditStudent/GroupsDropdown.vue'
import { groupModule } from '@/store/group/module'
import { instructorModule } from '@/store/instructor/module'
import { examModule } from '@/store/exam/module'
import { studentModule } from '@/store/student/module'
import { organizationModule } from '@/store/organization/module'

const {
    Table,
    Link: PocketLink,
    Button: PocketButton,
} = UIKit

type MappedGroup = {
    id: string
    name: string
    students: number
    creator: string
    hideRowButton: boolean
    creatorSortName: string
}

const props = defineProps<{
    orgStudentId: string
    studentName: string
}>()

const isLoading = ref(false)
const sortSettings = ref<ITableSortSettings | null>(null)
const showGroupsDropdown = ref(false)
const groupColumns = ref<ITableColumn[]>([{
    name: 'Groups',
    propName: 'name',
    styles: {
        width: '191px',
    },
}, {
    name: '# Students',
    propName: 'students',
    styles: {
        width: '80px',
        textAlign: 'center',
    },
    labelStyles: {
        textAlign: 'center',
    },
}, {
    name: 'Group Creator',
    propName: 'creator',
    styles: {
        width: '98px',
    },
}, {
    propName: 'remove',
    isLabelHidden: true,
    styles: {
        width: '58px',
    },
}])

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const availableGroups = computed(() => {
    const studentGroups = Object.values(filteredCompositeGroupsLibrary.value)
    const systemExamGroups = studentGroups.filter(g => g.type === 'system' && !g.owner)
    const orgExamIds = systemExamGroups.map(g => g.orgExam.objectId)
    const instructorId = currentOrgInstructor.value?.objectId

    return Object.values(currentOrgGroups.value)
        .filter(g => {
            const isGroupInStudentsExams = g.orgExam && orgExamIds.includes(g.orgExam.objectId)
            const isStudentAlreadyInGroup = groupModule.getters.getIsStudentInGroup({
                studentId: props.orgStudentId,
                groupId: g.objectId,
            })
            const isGroupEditable = g.type === 'public'
                || (g.type !== 'system' && instructorId && g.owner?.objectId === instructorId)

            return isGroupInStudentsExams
                && !isStudentAlreadyInGroup
                && isGroupEditable
        })
        .map(g => ({
            id: g.objectId,
            name: g.name,
            exam: currentOrgExams.value[g.orgExam.objectId]?.displayName || '--',
        }))
})

const filteredCurrentOrgGroups = computed(() => {
    return groupModule.getters.searchCurrentOrgGroups({ studentId: props.orgStudentId })
        .filter(g => !g.owner || (g.owner && g.type !== 'system'))
})

const filteredCompositeGroupsLibrary = computed(() => {
    return filteredCurrentOrgGroups.value.reduce((acc, g) => {
        const groupCompositeKey = groupModule.getters.getGroupCompositeKeyForGroupId(g.objectId)
        if (!groupCompositeKey) {
            return acc
        }
        if (!acc[groupCompositeKey]) {
            acc[groupCompositeKey] = g
        }
        return acc
    }, {} as { [ groupCompositeKey: string ]: Teach.Class.OrgGroupJSON })
})

const mappedGroups = computed(() => {
    const compositeGroupsLibrary = filteredCurrentOrgGroups.value
        .reduce((acc, g) => {
            let instructorName, instructorSortName
            if (!g.owner) {
                instructorName = 'System'
            } else if (currentOrgInstructor.value?.objectId === g.owner.objectId) {
                instructorName = 'You'
            } else {
                const orgInstructor = currentOrgInstructors.value?.[g.owner.objectId]
                const user = orgInstructor?.user as Study.Class.UserJSON
                instructorName = user && user.firstName && user.lastName
                    ? [ user.firstName, user.lastName ].join(' ')
                    : user.username
                instructorSortName = user && (user.firstName || user.lastName)
                    ? [ user.lastName, user.firstName ].join(' ')
                    : user.username
            }

            const groupCompositeKey = groupModule.getters.getGroupCompositeKeyForGroupId(g.objectId)
            if (!groupCompositeKey) {
                return acc
            }

            const mappedGroup = {
                id: g.objectId,
                name: g.name,
                students: g.orgStudents.length,
                creator: instructorName,
                hideRowButton: !g.owner
                    || (g.type === 'private' && currentOrgInstructor.value?.objectId !== g.owner.objectId),
                creatorSortName: instructorSortName || instructorName,
            }

            if (!acc[groupCompositeKey]) {
                acc[groupCompositeKey] = mappedGroup
            }

            return acc
        }, {} as { [ compositeKey: string ]: MappedGroup })

    return Object.values(compositeGroupsLibrary)
        .sort((a, b) => {
            if (sortSettings.value?.column) {
                if (sortSettings.value.column.name === 'Groups') {
                    return sortSettings.value.direction * a.name.localeCompare(b.name)
                }
                if (sortSettings.value.column.name === '# Students') {
                    return sortSettings.value.direction * (b.students - a.students)
                }
                if (sortSettings.value.column.name === 'Group Creator') {
                    return sortSettings.value.direction * a.creatorSortName.localeCompare(b.creatorSortName)
                }
            }

            return -1
        })
})

onMounted(async () => {
    await Promise.all([
        instructorModule.actions.fetchCurrentOrgInstructors(),
        groupModule.actions.fetchCurrentOrgGroups(),
        examModule.actions.fetchCurrentOrgExams(),
    ])
})

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const addGroup = async (groupId: string) => {
    if (!isLoading.value) {
        isLoading.value = true
        showGroupsDropdown.value = false
        await studentModule.actions.addStudentsToGroup({
            orgGroupId: groupId,
            orgStudentIds: [ props.orgStudentId ],
        })
        organizationModule.actions.validateOrgState()
        isLoading.value = false
    }
}

const removeGroup = async (groupId: string) => {
    if (!isLoading.value) {
        isLoading.value = true
        await studentModule.actions.removeStudentFromCompositeGroup({
            groupCompositeKey: groupModule.getters.getGroupCompositeKeyForGroupId(groupId) || '',
            orgStudentId: props.orgStudentId,
        })
        isLoading.value = false
    }
}
</script>


<template>
    <div class="edit-student-groups">
        <div class="edit-student-groups__title">
            {{ studentName }} is in {{ mappedGroups.length }} group{{ mappedGroups.length > 1 ? 's' : '' }}
        </div>
        <Table
            :rows="mappedGroups"
            :columns="groupColumns"
            :show-header="false"
            class="edit-student-groups__groups"
            :table-sort-icon-styles="{
                position: 'absolute', top: '50%', right: '-15px', marginTop: '-4.5px'
            }"
            @sort="updateSortSettings"
        >
            <template #tableCellValue="{ row, column }">
                <PocketButton
                    v-if="column.propName === 'remove' && !row.hideRowButton"
                    class="add-group__remove"
                    type="tertiary"
                    @click="removeGroup(row.id)"
                >
                    Remove
                </PocketButton>
            </template>
        </Table>
        <PocketLink
            class="edit-student-groups__add-group"
            :disabled="!availableGroups.length"
            @click.prevent="showGroupsDropdown = true"
        >
            + Add to group
        </PocketLink>
        <div class="edit-student-groups__group-dropdown-container">
            <GroupsDropdown
                v-if="showGroupsDropdown"
                class="edit-student-groups__group-dropdown"
                :org-student-id="orgStudentId"
                :available-groups="availableGroups"
                @close="showGroupsDropdown = false"
                @groupSelected="addGroup"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.edit-student-groups {
    margin-bottom: 45px;

    &__title {
        font-size: 14px;
        font-weight: 600;
        color: $slate-03;
        line-height: 19px;
        margin-left: 11px;
        margin-bottom: 24px;
    }

    &__add-group {
        padding: 12px 11px;
        font-weight: 600;
        display: block;
    }

    &__group-dropdown-container {
        position: relative;
        z-index: 1;
    }

    &__group-dropdown {
        position: absolute;
        top: -2px;
        left: 0;
    }
}
</style>
